import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { Copy, Share2, CheckCircle, Users, Gift, TrendingUp, Loader2 } from 'lucide-react';

/**
 * ReferralPage — Complete Refer & Earn page.
 * Shows referral code, share options, stats, and referral list.
 */

const API_BASE = '';

const ReferralPage = () => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Always read token fresh from localStorage
  const getToken = () => localStorage.getItem('reddyannabook_token');

  useEffect(() => {
    const fetchData = async () => {
      const token = getToken();
      if (!token) { setLoading(false); return; }
      setLoading(true);
      setError(null);

      // Fetch referral info
      try {
        const infoRes = await fetch(`${API_BASE}/api/referral/info`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const infoText = await infoRes.text();
        if (infoText) {
          const infoData = JSON.parse(infoText);
          if (infoData.data) setInfo(infoData.data);
        }
      } catch (err) {
        console.error('Referral info fetch error:', err);
        setError('Unable to load referral info');
      }

      // Fetch referral list
      try {
        const listRes = await fetch(`${API_BASE}/api/referral/list`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const listText = await listRes.text();
        if (listText) {
          const listData = JSON.parse(listText);
          if (listData.data?.referrals) setReferrals(listData.data.referrals);
        }
      } catch (err) {
        console.error('Referral list fetch error:', err);
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const token = getToken();

  // API returns: data.referral_code, data.stats.*, data.settings.*
  const referralCode = info?.referral_code || info?.my_code || '—';
  const stats = info?.stats || {};
  const settings = info?.settings || {};
  const referralLink = info?.referral_link || `${window.location.origin}/register?ref=${referralCode}`;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join REDDY ANNA BOOK',
          text: `Use my referral code ${referralCode} and get ₹${settings?.referee_bonus || 50} bonus!`,
          url: referralLink,
        });
      } catch {}
    } else {
      handleCopyLink();
    }
  };

  if (!token) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <div className="text-5xl mb-4">🎁</div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Refer & Earn</h2>
        <p className="text-gray-600 mb-4">Please login to access the referral program</p>
        <button
          onClick={() => navigate('/login')}
          className="px-6 py-2 font-bold rounded-lg transition-all"
          style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', color: '#0a0a0a', boxShadow: '0 4px 12px rgba(245,158,11,0.3)' }}
        >
          Login
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#f59e0b' }} />
        <p className="text-gray-500">Loading referral info...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-4xl mb-2">🎁</div>
        <h1 className="text-2xl font-bold text-gray-900">Refer & Earn</h1>
        <p className="text-sm text-gray-500 mt-1">Share your code and earn rewards</p>
      </div>

      {/* Referral Code Card */}
      <div
        className="rounded-xl p-6 text-white shadow-lg"
        style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', boxShadow: '0 8px 24px rgba(245,158,11,0.35)' }}
      >
        <div className="text-center mb-4">
          <p className="text-sm text-white/80 mb-1">Your Referral Code</p>
          <div className="flex items-center justify-center gap-3">
            <span className="text-3xl font-black tracking-widest bg-white/20 px-6 py-2 rounded-lg">
              {referralCode}
            </span>
            <button
              onClick={handleCopyCode}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              title="Copy code"
            >
              {copied ? <CheckCircle className="w-5 h-5 text-green-300" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          {copied && <p className="text-xs text-green-300 mt-1">Copied!</p>}
        </div>

        {/* Share buttons */}
        <div className="flex items-center justify-center gap-3 mt-4">
          <button onClick={handleCopyLink}
            className="flex items-center gap-1.5 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-semibold transition-colors">
            <Copy className="w-4 h-4" />
            {copiedLink ? 'Copied!' : 'Copy Link'}
          </button>
          <button onClick={handleShare}
            className="flex items-center gap-1.5 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-semibold transition-colors">
            <Share2 className="w-4 h-4" />
            Share
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Gift className="w-5 h-5" style={{ color: '#f59e0b' }} /> How it works
        </h3>
        <div className="space-y-3">
          {[
            { step: 1, text: 'Share your referral code with friends', icon: '📤' },
            { step: 2, text: 'Friend registers using your code', icon: '👤' },
            { step: 3, text: `Friend makes first deposit of ₹${settings?.min_deposit_required || 100}+`, icon: '💰' },
            { step: 4, text: `You earn ₹${settings?.referrer_bonus || 100} bonus!`, icon: '🎉' },
            { step: 5, text: `Friend gets ₹${settings?.referee_bonus || 50} welcome bonus`, icon: '🎁' },
          ].map(item => (
            <div key={item.step} className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                style={{ background: 'rgba(245,158,11,0.12)', color: '#d97706' }}
              >
                {item.step}
              </div>
              <span className="text-sm text-gray-700">{item.text}</span>
              <span className="ml-auto text-lg">{item.icon}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Referrals', value: stats?.total_referrals || 0, icon: <Users className="w-5 h-5" />, color: 'blue' },
          { label: 'Successful', value: stats?.completed_referrals || 0, icon: <CheckCircle className="w-5 h-5" />, color: 'green' },
          { label: 'Pending', value: stats?.pending_referrals || 0, icon: <Loader2 className="w-5 h-5" />, color: 'yellow' },
          { label: 'Total Earned', value: `₹${Number(stats?.total_earned || stats?.referral_bonus_earned || 0).toLocaleString('en-IN')}`, icon: <TrendingUp className="w-5 h-5" />, color: 'lotus' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className={`w-10 h-10 rounded-full bg-${stat.color === 'lotus' ? 'lotus' : stat.color}-100 flex items-center justify-center mx-auto mb-2 text-${stat.color === 'lotus' ? 'lotus' : stat.color}-600`}>
              {stat.icon}
            </div>
            <div className="text-lg font-bold text-gray-900">{stat.value}</div>
            <div className="text-[11px] text-gray-500">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Referral List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-[#2c3e50] text-white">
          <h3 className="font-bold text-sm">My Referrals ({referrals.length})</h3>
        </div>

        {referrals.length === 0 ? (
          <div className="p-6 text-center text-gray-400">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No referrals yet. Share your code to start earning!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {/* Header */}
            <div className="grid grid-cols-3 gap-0 px-4 py-2 bg-gray-50 text-[10px] font-bold text-gray-500 uppercase">
              <div>User</div>
              <div className="text-center">Status</div>
              <div className="text-right">Bonus</div>
            </div>
            {referrals.map((ref, idx) => (
              <div key={idx} className="grid grid-cols-3 gap-0 px-4 py-3 items-center hover:bg-gray-50">
                <div>
                  <div className="text-sm font-medium text-gray-800">{ref.username || ref.referred_username || '***'}</div>
                  <div className="text-[10px] text-gray-400">{ref.registered_at ? new Date(ref.registered_at).toLocaleDateString('en-IN') : ''}</div>
                </div>
                <div className="text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    ref.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {ref.status === 'completed' ? 'Completed' : 'Pending'}
                  </span>
                </div>
                <div className="text-right">
                  {ref.status === 'completed' ? (
                    <span className="text-sm font-bold text-green-600">+₹{Number(ref.bonus_earned || ref.bonus_amount || 0).toLocaleString('en-IN')}</span>
                  ) : (
                    <span className="text-xs text-gray-400">Waiting for deposit</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-sm text-red-600">{error}</p>
          <button onClick={fetchData} className="mt-2 text-xs text-red-700 underline">Try Again</button>
        </div>
      )}
    </div>
  );
};

export default ReferralPage;
